/* ==========================================================================
   BRITTEN CUSTOM: Britten homepage (v2) -- behaviour (one small vanilla module)
   ==========================================================================

   Added:       October 2026
   Author:      Claude / Matthew
   Location:    assets/britten-home.js
   Loaded by:   sections/britten-home.liquid (a deferred script tag, after the
                page markup). Nothing on the homepage is inline JavaScript.

   PURPOSE
   The three things on the new homepage that CSS cannot do: the "When do you
   need your veil?" date tool, the photo credit buttons and the press strip's
   pause button. No jQuery, no globals.

   HOW IT WORKS
   1. Date tool ([data-bhp-date], snippets/britten-home-date.liquid). Runs only
      when window.BrittenDeliveryCalc (the product pages' own delivery maths,
      on every other page in calc mode while Britten Core is on) and
      window.BrittenI18n are there. Otherwise the server sentence stays and the
      field stays hidden.
      - est = BrittenDeliveryCalc.estimate(undefined, []): the visitor's
        country, a standard veil, today's cut-off and UK bank holidays. The
        dates used are est.standard.maxISO and est.priority.maxISO.
      - No date: "Order today and a standard veil is with you by {std}, or by
        {rush} with rush." (the rush clause only when there is a rush estimate).
      - A date: standard arrives before it, the safe sentence with the gap;
        else rush arrives before it, the rush sentence with the gap; else "That
        is very close. Send us a message today ..." linking to
        {root}/pages/contact?type=general. The gap reads in weeks from 14 days
        ("3 weeks"), else in days ("1 day", "9 days"). Arriving ON the day she
        needs it is not "before", so the gap is never "0 days".
      - A past date (BrittenDeliveryCalc.weddingVerdict says 'past') shows the
        no-date sentence and is not kept.
      - Dates print in the page language: "Wednesday 14 October" in English
        (en-GB, as the product pages print them), "miércoles, 14 de octubre".
      - The date is kept in BrittenCore.prefs ('wedding_date') when the Core
        is loaded, else in localStorage britten_wedding_date (the key the Core
        mirrors), and pre-filled from the same place. The Core script loads
        after this one, so an empty, untouched field is filled again on
        britten:core:ready.
      - Metric: a britten:metric event named home_date_<verdict> (the bus and
        detail shape of snippets/britten-wedding-date-check.liquid), sent once
        the date has settled, never for a pre-filled date.
      - The iOS fixes of britten-wedding-date-check: the native input is an
        invisible overlay on the visible field; the "Select date" span shows
        while it is empty; on touch the date is committed on blur, because iOS
        fires change the moment the picker opens.
   2. Photo credits: each [data-bhp-credit] button toggles its own
      aria-expanded and the hidden attribute of the .bhp-credit__pop right
      after it. One open at a time; Escape, or a click anywhere else, closes it.
      On opening, the pop is anchored to the side of its figure with more
      screen room (class bhp-credit__pop--start = from the left edge) and given
      a max-width that fits, as the approved design does.
   3. Press pause: [data-bhp-press-pause] toggles aria-pressed and the class
      bhp-press--paused on its closest [data-bhp-press] (the CSS pauses the
      marquee). The label swaps between the button's data-label-pause and
      data-label-play attributes (translated in Liquid, so no English here). It
      goes into a [data-bhp-press-label] child when there is one, else the
      button's own text, and into aria-label when the button has one.

   THEME DEPENDENCY
   - snippets/britten-delivery-estimate.liquid (window.BrittenDeliveryCalc)
   - snippets/britten-i18n-js.liquid (window.BrittenI18n, keys js.home.*)
   - assets/britten-core.js (window.BrittenCore.prefs, optional)
   - assets/britten-home.css (.bhp-date__ph--set, .bhp-press--paused)

   CHANGELOG
   - October 2026: created (homepage v2 build).
   ========================================================================== */

(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false; // true: logs what init() found and what the date tool did
  var STORE_DATE = 'britten_wedding_date'; // legacy key, mirrored by the Britten Core
  var WEEKS_FROM = 14; // a gap this many days or longer reads in weeks
  var METRIC_DELAY = 600; // ms: one metric per settled date, not one per keystroke
  var CONTACT_PATH = '/pages/contact?type=general';
  var TOUCH = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  var DAY = 86400000;

  // === HELPER FUNCTIONS ===
  function log(message) {
    if (DEBUG_MODE && window.console) console.log('[Britten Home] ' + message);
  }

  function metric(name) {
    try {
      document.dispatchEvent(new CustomEvent('britten:metric', { detail: { name: name, data: {} } }));
    } catch (e) { /* metrics must never break the page */ }
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // YYYY-MM-DD to a local date at midnight, or null.
  function parseISO(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return null;
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    return isNaN(d.getTime()) ? null : d;
  }

  function pad(n) {
    return (n > 9 ? '' : '0') + n;
  }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  // The page language for DISPLAY only. English prints en-GB ("Wednesday
  // 14 October"); a bare "en" would give the US order.
  var LANG = (function () {
    var l = String(document.documentElement.getAttribute('lang') || 'en');
    return l.toLowerCase().indexOf('en') === 0 ? 'en-GB' : l;
  })();

  function formatter(opts) {
    try {
      return new Intl.DateTimeFormat(LANG, opts);
    } catch (e) {
      try { return new Intl.DateTimeFormat('en-GB', opts); } catch (e2) { return null; }
    }
  }
  var fmtDay = formatter({ weekday: 'long', day: 'numeric', month: 'long' });
  var fmtField = formatter({ day: 'numeric', month: 'long', year: 'numeric' });

  function dayText(d) {
    return fmtDay ? fmtDay.format(d) : d.toDateString();
  }

  function fieldText(d) {
    return fmtField ? fmtField.format(d) : d.toDateString();
  }

  // --- The stored wedding date: the Core's prefs when loaded, else the legacy key ---
  function corePrefs() {
    var core = window.BrittenCore;
    return core && core.prefs && typeof core.prefs.set === 'function' ? core.prefs : null;
  }

  function readDate() {
    var p = corePrefs();
    var v = null;
    if (p && typeof p.get === 'function') v = p.get('wedding_date');
    if (!v) {
      try { v = localStorage.getItem(STORE_DATE); } catch (e) { v = null; }
    }
    return parseISO(v) ? v : null;
  }

  function saveDate(iso) {
    var p = corePrefs();
    if (p) {
      p.set('wedding_date', iso || null);
      return;
    }
    try {
      if (iso) localStorage.setItem(STORE_DATE, iso);
      else localStorage.removeItem(STORE_DATE);
    } catch (e) { /* private mode: the date simply is not kept */ }
  }

  // === PHOTO CREDITS ===
  // As the approved design: the pop opens towards the side of the screen with
  // more room (from the figure's left edge rightwards, or from its right edge
  // leftwards), as wide as fits, so a small thumbnail's credit stays readable
  // and on screen.
  function placePop(btn, pop) {
    var fig = btn.closest('figure') || btn.parentNode;
    var r = fig.getBoundingClientRect();
    var vw = document.documentElement.clientWidth || window.innerWidth || 0;
    var roomRight = vw - r.left;
    var roomLeft = r.right;
    var start = roomRight > roomLeft;
    pop.classList.toggle('bhp-credit__pop--start', start);
    pop.style.maxWidth = Math.max(140, Math.min(260, (start ? roomRight : roomLeft) - 18)) + 'px';
  }

  function setCredit(btn, open) {
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var pop = btn.nextElementSibling;
    if (!pop || !pop.classList.contains('bhp-credit__pop')) return;
    if (open) placePop(btn, pop);
    pop.hidden = !open;
  }

  function closeCredits(home, keep) {
    var open = home.querySelectorAll('[data-bhp-credit][aria-expanded="true"]');
    for (var i = 0; i !== open.length; i++) {
      if (open[i] !== keep) setCredit(open[i], false);
    }
  }

  // === PRESS PAUSE ===
  function togglePress(btn) {
    var box = btn.closest('[data-bhp-press]');
    if (!box) return;
    var paused = btn.getAttribute('aria-pressed') !== 'true';
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    box.classList.toggle('bhp-press--paused', paused);
    var label = btn.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
    if (label) {
      var slot = btn.querySelector('[data-bhp-press-label]');
      if (slot) slot.textContent = label;
      else if (!btn.children.length) btn.textContent = label;
      if (btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', label);
    }
    log('press ' + (paused ? 'paused' : 'playing'));
  }

  function initClicks(home) {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var btn = t.closest('[data-bhp-credit]');
      if (btn && home.contains(btn)) {
        // A credit can sit inside a linked tile: the tap must not follow the link.
        e.preventDefault();
        var open = btn.getAttribute('aria-expanded') !== 'true';
        closeCredits(home, btn);
        setCredit(btn, open);
        return;
      }

      var pop = t.closest('.bhp-credit__pop');
      if (pop && home.contains(pop)) {
        // Reading or selecting the credit keeps it open (and never follows a
        // tile link round it); a link inside the credit itself still works.
        var link = t.closest('a');
        if (!link || !pop.contains(link)) e.preventDefault();
        return;
      }

      closeCredits(home, null);

      var pause = t.closest('[data-bhp-press-pause]');
      if (pause && home.contains(pause)) togglePress(pause);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') closeCredits(home, null);
    });
  }

  // === DATE TOOL ===
  function initDate(home) {
    var box = home.querySelector('[data-bhp-date]');
    if (!box) return;

    var calc = window.BrittenDeliveryCalc;
    // The receiver must end in "i18n": scripts/i18n-js-keys-check.py finds call sites that way.
    var i18n = window.BrittenI18n;
    if (!calc || typeof calc.estimate !== 'function' || !i18n || typeof i18n.t !== 'function') {
      log('date: no delivery calculation or string table, the server sentence stays');
      return;
    }

    var est = null;
    try { est = calc.estimate(undefined, []); } catch (e) { log('date: estimate failed: ' + e.message); }
    var std = est && est.standard ? parseISO(est.standard.maxISO) : null;
    if (!std) {
      log('date: no standard date, the server sentence stays');
      return;
    }
    var rush = est.priority && est.priority.maxISO ? parseISO(est.priority.maxISO) : null;

    var field = box.querySelector('[data-bhp-date-field]');
    var input = box.querySelector('[data-bhp-date-input]');
    var ph = box.querySelector('[data-bhp-date-ph]');
    var out = box.querySelector('[data-bhp-date-sentence]');
    if (!field || !input || !ph || !out) return;

    var root = box.getAttribute('data-root') || '';
    var emptyText = ph.textContent;
    var committed = null; // the date the sentence answers
    var touched = false; // she has used the field on this page
    var pending = null; // touch: the value held while the picker is open
    var timer = null;

    function verdictFor(iso) {
      if (typeof calc.weddingVerdict !== 'function') return null;
      try { return calc.weddingVerdict(est, iso); } catch (e) { return null; }
    }

    function gapText(from, need) {
      var days = Math.round((need.getTime() - from.getTime()) / DAY);
      if (days >= WEEKS_FROM) {
        var weeks = Math.floor(days / 7);
        return i18n.n('js.home.gap_weeks', weeks, { weeks: weeks });
      }
      return i18n.n('js.home.gap_days', days, { days: days });
    }

    function sentence(iso) {
      var need = iso ? parseISO(iso) : null;
      if (!need) {
        if (rush) return i18n.t('js.home.default_html', { std: esc(dayText(std)), rush: esc(dayText(rush)) });
        return i18n.t('js.home.default_std_html', { std: esc(dayText(std)) });
      }
      if (need.getTime() > std.getTime()) {
        return i18n.t('js.home.safe_html', { std: esc(dayText(std)), gap: esc(gapText(std, need)) });
      }
      if (rush && need.getTime() > rush.getTime()) {
        return i18n.t('js.home.rush_html', { rush: esc(dayText(rush)), gap: esc(gapText(rush, need)) });
      }
      return i18n.t('js.home.close_html', { url: esc(root + CONTACT_PATH) });
    }

    function commit(value, quiet) {
      var iso = parseISO(value) ? value : null;
      var verdict = iso ? verdictFor(iso) : null;
      var past = verdict === 'past';
      committed = iso;

      // The visible field shows what she chose; the native input is invisible.
      ph.textContent = iso ? fieldText(parseISO(iso)) : emptyText;
      ph.classList.toggle('bhp-date__ph--set', !!iso);

      var html = sentence(iso && !past ? iso : null);
      if (html) out.innerHTML = html;

      if (quiet) return;
      if (!iso) saveDate(null);
      else if (!past) saveDate(iso);
      if (verdict) {
        clearTimeout(timer);
        timer = setTimeout(function () { metric('home_date_' + verdict); }, METRIC_DELAY);
      }
      log('date: ' + (iso || 'cleared') + (verdict ? ' (' + verdict + ')' : ''));
    }

    input.min = todayISO();

    input.addEventListener('change', function () {
      touched = true;
      var v = input.value || null;
      if (TOUCH && document.activeElement === input) {
        pending = { v: v }; // the picker is still open: commit on blur, not now
        return;
      }
      commit(v);
    });

    input.addEventListener('blur', function () {
      if (!TOUCH) return;
      var held = pending;
      pending = null;
      var v = held ? held.v : (input.value || null);
      // Opened and dismissed with no change: nothing to do.
      if (held || v !== committed) commit(v);
    });

    // The input is an invisible overlay, so a click anywhere on the field opens the picker.
    input.addEventListener('click', function () {
      touched = true;
      try { input.showPicker(); } catch (e) { /* older browsers open it themselves */ }
    });

    // A returning bride: her date, answered straight away (no metric, nothing saved).
    var saved = readDate();
    if (saved && verdictFor(saved) !== 'past') {
      input.value = saved;
      commit(saved, true);
    } else {
      commit(null, true);
    }
    field.hidden = false;
    out.setAttribute('aria-live', 'polite');

    // The Core loads after this file. If it holds a date the legacy key did not, use it.
    document.addEventListener('britten:core:ready', function () {
      if (touched || committed) return;
      var later = readDate();
      if (later && verdictFor(later) !== 'past') {
        input.value = later;
        commit(later, true);
      }
    });

    log('date: ready, standard ' + est.standard.maxISO + (rush ? ', rush ' + est.priority.maxISO : ', no rush'));
  }

  // === MAIN ===
  function init() {
    if (!FEATURE_ENABLED) return;
    var home = document.querySelector('.britten-home');
    if (!home) {
      log('no .britten-home on this page');
      return;
    }
    initClicks(home);
    initDate(home);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
